import { ReactNode, useEffect } from 'react'
import { faCheck, faStar } from '@fortawesome/pro-light-svg-icons'
import {
  Button,
  Text,
  Icon,
  Accordion,
  Card,
  Separator,
  Badge,
  Image,
  CardColors,
} from '@/ui'
import { useTherapistFlag, CaseStyle } from '@/utils/launchdarklyHelpers'
import { usePayrollPrices } from '../payrollPrice.hooks'
import { useAnalyticsTrack, useAnalyticsView } from '@/features/amplitude'
import { useNavigateWithLocation } from '@/utils/routeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import {
  selectIsCurrentUserScorp,
  selectIsGroupPractice,
} from '@/selectors/user.selectors'
import { TextColors, TypographyStyles } from '@/styles'
import { ProductFeatureId } from '@/actions/permissionActions'
import { selectUserHasPermission } from '@/reducers/auth/permission.selectors'
import { PayrollVariant } from './payrollVariant.constants'

export const GreenCheckRow = ({
  content,
}: {
  content: ReactNode
  short?: boolean
}) => (
  <div className="flex flex-row gap-2">
    <div className="w-6 flex-shrink-0">
      <Icon icon={faCheck} color="text-green-500" />
    </div>
    <div className="flex-1">{content}</div>
  </div>
)

export const PayrollIntroCard = ({
  showStartButton = false,
}: {
  showStartButton?: boolean
}) => {
  const navigate = useNavigateWithLocation()
  const track = useAnalyticsTrack()
  const hasFreePayrollWithSubscription = useReselector(
    selectUserHasPermission,
    ProductFeatureId.payrollIncludedForFreeForSelf
  )
  const { base, seat } = usePayrollPrices()
  const feeWording = base
    ? 'Plus a monthly fee per employee'
    : 'A monthly base fee plus a fee per employee'
  const perPersonLine = seat ? `Plus ${seat} per employee / month` : feeWording

  return (
    <>
      <Card color={CardColors.PRIORITY} className="p-6">
        {hasFreePayrollWithSubscription && (
          <Badge color="green" variant="secondary">
            <Icon icon={faStar} color="green" />
            Member Benefit
          </Badge>
        )}
        <Text variant={TypographyStyles.DISPLAY_SM} className="mt-4">
          Heard Payroll
        </Text>

        <Text
          variant={TypographyStyles.H2}
          color={TextColors.MUTED}
          className="mt-1"
        >
          Compliance made easy with streamlined features
        </Text>

        <Image
          src="https://heard-images.s3.amazonaws.com/assets/checkbooks.svg"
          className="w-auto h-[162px] bg-transparent mx-auto"
          alt="Checkbooks"
        />

        {showStartButton && (
          <Button
            onClick={() => {
              track('clicked payroll upsell welcome page button')
              navigate('/payroll/interest')
            }}
            className="w-full"
          >
            Get Started
          </Button>
        )}

        <div className="flex justify-center gap-1 pt-4">
          <Text variant={TypographyStyles.H3} color={TextColors.MUTED}>
            Powered by
          </Text>
          <Image
            src="https://heard-images.s3.amazonaws.com/assets/gusto_logo.svg"
            className="w-auto h-5"
            alt="Gusto Logo"
          />
        </div>
      </Card>

      {!hasFreePayrollWithSubscription && (
        <div className="text-center pt-4 space-y-4">
          {base && (
            <>
              <Text variant={TypographyStyles.DISPLAY_SM} className="inline">
                {base}
              </Text>
              <Text variant={TypographyStyles.DISPLAY_SM} className="inline">
                /month
              </Text>
            </>
          )}
          <Text variant={TypographyStyles.BODY_MD} className="text-center">
            {perPersonLine}
          </Text>
          <Text
            variant={TypographyStyles.BODY_MD}
            color={TextColors.MUTED}
            className="text-center"
          >
            If you migrate your Gusto payroll, you’ll only be charged by Heard
            going forward.
          </Text>
        </div>
      )}
    </>
  )
}

const LearnMoreFAQs = () => {
  const isScorp = useReselector(selectIsCurrentUserScorp)
  const isGroupPractice = useReselector(selectIsGroupPractice)
  const replacementTextPlural = useTherapistFlag(CaseStyle.lowerCase, true)

  const pickVariant = (): PayrollVariant => {
    if (isScorp) return PayrollVariant.scorp
    if (isGroupPractice) return PayrollVariant.group
    return PayrollVariant.general
  }
  const variant = pickVariant()

  const whyPayroll: Record<PayrollVariant, ReactNode> = {
    [PayrollVariant.scorp]: (
      <Text variant={TypographyStyles.BODY_SM}>
        The IRS requires S Corporation owners to receive a &apos;reasonable
        salary&apos; through payroll rather than taking distributions only.
        Using payroll ensures you correctly calculate and withhold payroll
        taxes, keeping your business in compliance.
      </Text>
    ),
    [PayrollVariant.group]: (
      <Text>
        Group practice owners generally need to manage payroll if they have any
        employees on staff as W-2 workers, which means they are required to
        withhold taxes and pay their employees a salary or hourly wage.
        <br />
        <br />
        If you only utilize independent contractors (1099s), you may not need to
        handle payroll for those individuals as they are responsible for their
        own taxes.
      </Text>
    ),
    [PayrollVariant.general]: (
      <Text variant={TypographyStyles.BODY_SM}>
        You need payroll if you pay anyone a salary or hourly wage as a W-2
        employee, including yourself once your practice is taxed as an S
        Corporation.
        <br />
        <br />
        If you only work with independent contractors (1099s), they are
        responsible for their own taxes and you may not need payroll for them.
      </Text>
    ),
  }

  const solutionFor: Record<PayrollVariant, string> = {
    [PayrollVariant.scorp]: `S Corporation ${replacementTextPlural}`,
    [PayrollVariant.group]: 'group practices',
    [PayrollVariant.general]: 'your practice',
  }

  return (
    <div className="flex flex-col gap-6">
      <Accordion type="single" collapsible>
        <Accordion.Item value="why-payroll">
          <Accordion.Trigger>Why do I need payroll?</Accordion.Trigger>
          <Accordion.Content>{whyPayroll[variant]}</Accordion.Content>
        </Accordion.Item>
      </Accordion>

      <Accordion type="single" collapsible>
        <Accordion.Item value="features">
          <Accordion.Trigger>
            What features are included in Heard payroll?
          </Accordion.Trigger>
          <Accordion.Content>
            <div className="flex flex-col gap-3">
              <Text variant={TypographyStyles.BODY_SM}>
                Heard Payroll is setup to be a simple, straightforward solution
                for {solutionFor[variant]}. We provide exactly what you need to
                stay compliant:
              </Text>

              <GreenCheckRow
                short
                content={
                  <Text variant={TypographyStyles.BODY_SM}>
                    Automatic Payroll on a set schedule
                  </Text>
                }
              />
              <GreenCheckRow
                short
                content={
                  <Text variant={TypographyStyles.BODY_SM}>
                    Direct deposit of funds
                  </Text>
                }
              />
              <GreenCheckRow
                short
                content={
                  <Text variant={TypographyStyles.BODY_SM}>
                    Calculation and withholding of state and federal payroll
                    taxes
                  </Text>
                }
              />
              <GreenCheckRow
                short
                content={
                  <Text variant={TypographyStyles.BODY_SM}>
                    Payroll documents automatically imported into Heard
                  </Text>
                }
              />
              <GreenCheckRow
                short
                content={
                  <Text variant={TypographyStyles.BODY_SM}>
                    Payroll history
                  </Text>
                }
              />

              <Text variant={TypographyStyles.BODY_SM}>
                If you have other employees, you can also:
              </Text>

              <GreenCheckRow
                short
                content={
                  <Text variant={TypographyStyles.BODY_SM}>
                    Pay contractors and hourly employees
                  </Text>
                }
              />
              <GreenCheckRow
                short
                content={
                  <Text variant={TypographyStyles.BODY_SM}>
                    Provide workers’ compensation
                  </Text>
                }
              />
              <GreenCheckRow
                short
                content={
                  <Text variant={TypographyStyles.BODY_SM}>
                    Flexible means for single-rate compensation (e.g. overtime,
                    bonuses, reimbursements)
                  </Text>
                }
              />
            </div>
          </Accordion.Content>
        </Accordion.Item>
      </Accordion>

      <Accordion type="single" collapsible>
        <Accordion.Item value="advanced-features">
          <Accordion.Trigger>
            Can I access more advanced payroll features if needed?
          </Accordion.Trigger>
          <Accordion.Content>
            <Text variant={TypographyStyles.BODY_SM}>
              At this time, Heard Payroll is setup to be a simple,
              straightforward solution for self-employed {replacementTextPlural}
              . We provide what you need to stay compliant, so not all features
              are supported. The following features are not currently supported:
              <ul className="list-disc pl-6 mt-2">
                <li>Gusto Premium or Contractor Only plans</li>
                <li>Gusto-sponsored or employee benefits</li>
                <li>Time tracking</li>
                <li>100% commission-based payments</li>
                <li>Multiple pay rates for a teammate</li>
              </ul>
            </Text>
          </Accordion.Content>
        </Accordion.Item>
      </Accordion>
    </div>
  )
}

const whyAddPayroll = [
  {
    title: 'Keep it simple',
    description: (
      <Text variant={TypographyStyles.BODY_MD}>
        Our streamlined set of features means you have exactly what you need to
        stay compliant.
      </Text>
    ),
  },
  {
    title: 'All-in-one place management',
    description: (
      <Text variant={TypographyStyles.BODY_MD}>
        Reduce the hassle of juggling multiple tools by managing your
        bookkeeping, taxes, and payroll all in one place.
      </Text>
    ),
  },
  {
    title: 'Simplifies tax forms',
    description: (
      <Text variant={TypographyStyles.BODY_MD}>
        Easily generate and store W-2 and 1099 forms for employees and
        contractors come tax time.
      </Text>
    ),
  },
  {
    title: 'Payroll taxes filed for you',
    description: (
      <Text variant={TypographyStyles.BODY_MD}>
        Gusto files payroll taxes for you every time you run payroll.
      </Text>
    ),
  },
  {
    title: 'Hands-on support and advice',
    description: (
      <Text variant={TypographyStyles.BODY_MD}>
        Our payroll team can provide ongoing analysis.
      </Text>
    ),
  },
]

const RightSideContent = () => {
  const hasFreePayrollWithSubscription = useReselector(
    selectUserHasPermission,
    ProductFeatureId.payrollIncludedForFreeForSelf
  )
  const { seat, yearly } = usePayrollPrices()
  const valueNote = yearly ? ` (${yearly} value)` : ''
  const savings = yearly ? ` ${yearly}/year` : ''
  const additionalEmployees = seat
    ? `Additional employees cost ${seat}/individual per month.`
    : 'Additional employees are billed per individual each month.'

  return (
    <div className="flex flex-col gap-2 mt-6">
      <Text variant={TypographyStyles.DISPLAY_SM}>
        Why setup payroll with Heard?
      </Text>
      <br />
      {hasFreePayrollWithSubscription ? (
        <>
          <Text variant={TypographyStyles.BODY_MD}>
            Pay yourself (and your team) and maintain compliance with our
            streamlined payroll solution. Manage your payroll seamlessly as part
            of your Heard membership{valueNote}.
          </Text>
          <br />
          <Text variant={TypographyStyles.BODY_MD} color={TextColors.MUTED}>
            Already using Gusto? Switch to Heard and save{savings}.{' '}
            {additionalEmployees}
          </Text>
        </>
      ) : (
        <Text variant={TypographyStyles.BODY_MD}>
          Pay your team and maintain compliance with our streamlined payroll
          solution. Manage your payroll seamlessly all in Heard.
        </Text>
      )}
      <br />
      <div>
        {whyAddPayroll.map(({ title, description }) => (
          <GreenCheckRow
            key={title}
            content={
              <>
                <Text variant={TypographyStyles.H3} className="mb-2">
                  {title}
                </Text>
                <Text variant={TypographyStyles.BODY_MD}>{description}</Text>
              </>
            }
          />
        ))}
      </div>
      <br />
      <Separator />
      <br />
      <Text variant={TypographyStyles.H2}>Learn more</Text>
      <br />
      <LearnMoreFAQs />
    </div>
  )
}

export const WelcomeToPayroll = () => {
  const pageView = useAnalyticsView()

  useEffect(() => {
    pageView('payroll upsell welcome')
  }, [pageView])

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col lg:flex-row gap-6">
        <div className="w-full lg:w-1/2 flex flex-col">
          <PayrollIntroCard showStartButton />
        </div>

        <div className="w-full lg:w-1/2">
          <RightSideContent />
        </div>
      </div>
    </div>
  )
}
